import { useState, useRef, useEffect } from 'react'
import { Box, Group, Text, ScrollArea, Textarea, ActionIcon, Loader, Tooltip } from '@mantine/core'
import { IconArrowLeft, IconSend, IconChevronsUp, IconChevronsDown } from '@tabler/icons-react'
import { useSetAtom } from 'jotai'
import { activeChatCharacterAtom } from '../../atoms/chatAtoms'
import { useCharacterChat } from '../../hooks/useCharacterChat'
import { useSpeech } from '../../hooks/useSpeech'
import { ChatMessage } from './ChatMessage'

// Build a speak descriptor for a character chat message. The id is stable per
// (character, message index) so it doesn't replay on every render.
function chatSegment({ storyId, character, text, index }) {
  if (!storyId || !character?.id || !text?.trim()) return null
  return {
    id: `chat:${character.id}:${index}`,
    kind: 'chat',
    storyId,
    characterId: character.id,
    text,
  }
}

export function ChatPanel({ character, storyId, serverSessionId, expanded, onToggleExpand }) {
  const setActiveChat = useSetAtom(activeChatCharacterAtom)
  const [inputValue, setInputValue] = useState('')
  const viewportRef = useRef(null)
  const { autoPlay, playSegment } = useSpeech()
  const lastSpokenRef = useRef(null)

  const {
    messages,
    streamingMessage,
    isLoading,
    isSending,
    sendMessage,
    error,
  } = useCharacterChat(storyId, character?.id, serverSessionId)

  // Auto-scroll to bottom when messages or streaming content changes
  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight
    }
  }, [messages, streamingMessage?.text])

  // Auto-play: when auto-play is on (resolved), speak each newly completed
  // assistant message once. We wait until streaming finishes (streamingMessage
  // cleared) and only speak the latest assistant message we haven't spoken yet.
  // Manual ▶ on each message still works regardless of this setting.
  useEffect(() => {
    if (!autoPlay || streamingMessage) return
    const lastIdx = messages.length - 1
    const last = messages[lastIdx]
    if (!last || last.role !== 'assistant') return
    const segment = chatSegment({ storyId, character, text: last.content, index: lastIdx })
    if (!segment || lastSpokenRef.current === segment.id) return
    lastSpokenRef.current = segment.id
    playSegment(segment)
  }, [messages, streamingMessage, autoPlay, storyId, character, playSegment])

  // Scroll to bottom when collapsing back (after grid transition completes)
  useEffect(() => {
    if (!expanded && viewportRef.current) {
      const timer = setTimeout(() => {
        if (viewportRef.current) {
          viewportRef.current.scrollTop = viewportRef.current.scrollHeight
        }
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [expanded])

  const handleSend = () => {
    if (!inputValue.trim() || isSending) return
    sendMessage(inputValue)
    setInputValue('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Box
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(to top, rgba(6, 14, 26, 0.96), rgba(10, 22, 40, 0.92))',
        borderTop: '1px solid rgba(0, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <Box px="lg" pt="md" pb="xs">
        <Group justify="space-between">
          <Text size="md" fw={700} c="cyan.3" ff="Orbitron, sans-serif">
            {character?.name}
          </Text>
          <Group gap={6}>
            <Tooltip label="Back to crew" position="bottom">
              <ActionIcon
                variant="subtle"
                color="cyan"
                size="md"
                onClick={() => setActiveChat(null)}
              >
                <IconArrowLeft size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={expanded ? 'Collapse panel' : 'Expand panel'} position="bottom">
              <ActionIcon
                variant="light"
                color="cyan"
                size="md"
                onClick={onToggleExpand}
                style={{
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  boxShadow: '0 0 8px rgba(0, 255, 255, 0.15)',
                }}
              >
                {expanded ? <IconChevronsDown size={18} /> : <IconChevronsUp size={18} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      {/* No large portrait banner: the chat is identical collapsed or expanded —
          just the transcript (each assistant bubble carries its own small avatar
          thumbnail, see ChatMessage) and the input. The old expanding portrait
          banner crowded out the transcript/input in the short collapsed panel. */}

      {/* Message list */}
      <ScrollArea
        viewportRef={viewportRef}
        style={{ flex: 1, minHeight: 0 }}
        px="lg"
        py="xs"
      >
        {isLoading ? (
          <Group gap="sm" justify="center" py="xl">
            <Loader size="xs" color="cyan" />
            <Text size="sm" c="dimmed" fs="italic">Loading conversation...</Text>
          </Group>
        ) : error ? (
          <Text size="sm" c="red.4" ta="center" py="xl">
            Failed to load conversation
          </Text>
        ) : messages.length === 0 && !streamingMessage ? (
          <Group gap="sm" justify="center" py="xl">
            {isSending && <Loader size="xs" color="cyan" />}
            <Text size="sm" c="dimmed" ta="center" fs="italic" ff="Rajdhani, sans-serif">
              {isSending ? `${character?.name} is preparing to speak...` : `Start a conversation with ${character?.name}`}
            </Text>
          </Group>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <ChatMessage
                key={idx}
                role={msg.role}
                content={msg.content}
                characterName={character?.name}
                characterAvatar={character?.avatar_url}
                timestamp={msg.metadata?.created_at}
                speakSegment={msg.role === 'assistant'
                  ? chatSegment({ storyId, character, text: msg.content, index: idx })
                  : null}
              />
            ))}
            {streamingMessage && !streamingMessage.complete && (
              <ChatMessage
                role="assistant"
                content={streamingMessage.text}
                characterName={character?.name}
                characterAvatar={character?.avatar_url}
                isStreaming
              />
            )}
          </>
        )}
      </ScrollArea>

      {/* Input bar */}
      <Box
        px="lg"
        py="sm"
        style={{
          borderTop: '1px solid rgba(0, 255, 255, 0.15)',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-end',
        }}
      >
        <Textarea
          placeholder={`Message ${character?.name || 'character'}...`}
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          autosize
          maxRows={3}
          disabled={isSending || isLoading}
          styles={{
            root: { flex: 1 },
            input: {
              background: 'rgba(10, 22, 40, 0.8)',
              border: '1px solid rgba(0, 229, 255, 0.2)',
              color: '#e0e8f0',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '1.05rem',
              lineHeight: 1.5,
              resize: 'none',
            },
          }}
        />
        <ActionIcon
          variant="light"
          color="cyan"
          size="lg"
          onClick={handleSend}
          disabled={!inputValue.trim() || isSending}
          style={{
            border: '1px solid rgba(0, 255, 255, 0.3)',
            boxShadow: '0 0 8px rgba(0, 255, 255, 0.15)',
            marginBottom: 1,
          }}
        >
          {isSending ? <Loader size={16} color="cyan" /> : <IconSend size={18} />}
        </ActionIcon>
      </Box>
    </Box>
  )
}
